import React from 'react';
import { Card, CardContent, TextField, Button, Box, Typography } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import EditIcon from '@mui/icons-material/Edit';
import { useLanguage } from '../../../../context/LanguageContext';
import { StoreAutocomplete } from '../../../../components/Common';
import { Store } from '../../../../types/Store';

interface StoreSelectorProps {
    stores: Store[];
    selectedStoreId: string | number | null;
    entityId: string;
    onStoreChange: (storeId: string | number) => void;
    onEntityIdChange: (entityId: string) => void;
    onShowClick: () => void;
    onEditClick: () => void;
    isEditable?: boolean;
    hideShowButton?: boolean;
}

const StoreSelector = ({
    stores,
    selectedStoreId,
    entityId,
    onStoreChange,
    onEntityIdChange,
    onShowClick,
    onEditClick,
    isEditable = false,
    hideShowButton = false,
}: StoreSelectorProps) => {
    const { t } = useLanguage();

    return (
        <Card sx={{ 
            borderRadius: 1, 
            boxShadow: 'none', 
            border: '1px solid #e0e0e0',
            mb: 2
        }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#6c757d', fontWeight: 500, textTransform: 'uppercase', fontSize: '0.875rem' }}>
                    {t('pages.store.selectStore')}
                </Typography>
                <Box sx={{ 
                    display: 'flex', 
                    gap: 1.5, 
                    alignItems: 'center',
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                    maxWidth: { md: '65%' }
                }}>
                    <Box sx={{ flex: { xs: '1 1 100%', md: '2' } }}>
                        <StoreAutocomplete
                            stores={stores}
                            value={selectedStoreId ?? ''}
                            onChange={(id) => onStoreChange(id)}
                            label={t('pages.store.store')}
                        />
                    </Box>
                    <Box sx={{ flex: { xs: '1 1 100%', md: '1' } }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t('pages.store.entityId')}
                            value={entityId}
                            onChange={(e) => onEntityIdChange(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    onShowClick();
                                }
                            }}
                            sx={{
                                '& .MuiInputLabel-root': { 
                                    fontSize: '0.875rem',
                                    color: '#6c757d'
                                },
                                '& .MuiOutlinedInput-root': {
                                    fontSize: '0.875rem',
                                    '& fieldset': { borderColor: '#ced4da' },
                                    '&:hover fieldset': { borderColor: '#adb5bd' },
                                    '&.Mui-focused fieldset': { borderColor: '#556ee6', borderWidth: '1px' },
                                },
                            }}
                        />
                    </Box>
                    {!hideShowButton && (
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<FilterAltIcon />}
                            onClick={onShowClick}
                            sx={{
                                px: 2.5,
                                minWidth: 'auto',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                background: '#556ee6',
                                textTransform: 'none',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                boxShadow: 'none',
                                '&:hover': { 
                                    background: '#4458d2',
                                    boxShadow: 'none'
                                },
                            }}
                        >
                            {t('pages.store.show')}
                        </Button>
                    )}
                    {!isEditable && (
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={onEditClick}
                            sx={{
                                px: 2.5,
                                minWidth: 'auto',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                background: '#556ee6',
                                textTransform: 'none',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                boxShadow: 'none',
                                '&:hover': { 
                                    background: '#4458d2',
                                    boxShadow: 'none'
                                },
                            }}
                        >
                            {t('common.modify')}
                        </Button>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default StoreSelector;
